/**
 * Client HTTP de l'application.
 *
 * Un seul point de passage vers l'API : c'est là que la session expirée est
 * détectée, que les erreurs FastAPI deviennent des messages lisibles, et que le
 * cookie est joint à chaque appel.
 */

import type {
  AmazonTemplateInfo,
  CatalogAnalysis,
  ConversionResponse,
  Identity,
  Meta,
  Profile,
  ProfileSummary,
  ProfileVersionSummary,
  RuleSet,
  SomReferenceSettings,
  SomSummary,
  SupplierDownload,
} from './types'

const BASE = '/api'

/** Erreur d'API portant le code HTTP, pour que l'appelant puisse le distinguer. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** La session a expiré ou n'a jamais existé. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

/** Prévient l'application qu'il faut repasser par l'écran de connexion. */
type UnauthorizedHandler = () => void
let onUnauthorized: UnauthorizedHandler = () => {}

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  onUnauthorized = handler
}

/**
 * FastAPI renvoie `detail`, tantôt une chaîne, tantôt la liste des erreurs de
 * validation Pydantic. On en tire une phrase affichable dans les deux cas.
 */
async function readError(response: Response): Promise<string> {
  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    return `Erreur ${response.status}`
  }

  const detail = (payload as { detail?: unknown })?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) => {
        const entry = item as { loc?: unknown[]; msg?: string }
        const field = Array.isArray(entry.loc) ? entry.loc.slice(1).join(' → ') : ''
        return field ? `${field} : ${entry.msg ?? ''}` : (entry.msg ?? '')
      })
      .filter(Boolean)
    if (messages.length) return messages.slice(0, 3).join(' ; ')
  }
  return `Erreur ${response.status}`
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${BASE}${path}`, {
      credentials: 'same-origin',
      ...init,
      headers: {
        Accept: 'application/json',
        ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
        ...init.headers,
      },
    })
  } catch {
    throw new ApiError(0, "Le serveur n'a pas répondu. Vérifiez votre connexion.")
  }

  if (response.status === 401) {
    onUnauthorized()
    throw new ApiError(401, 'Session expirée. Reconnectez-vous.')
  }
  if (!response.ok) throw new ApiError(response.status, await readError(response))
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

// --- Authentification ------------------------------------------------------

export const auth = {
  login: (username: string, password: string) =>
    request<Identity>('/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  logout: () => request<void>('/auth/logout', { method: 'POST' }),
  me: () => request<Identity>('/auth/me'),
}

// --- Référentiel -----------------------------------------------------------

export const meta = {
  get: () => request<Meta>('/meta'),
}

export const amazonTemplate = {
  get: () => request<AmazonTemplateInfo>('/amazon-template'),
  upload: (file: File) => {
    const body = new FormData()
    body.append('file', file)
    return request<AmazonTemplateInfo>('/amazon-template', { method: 'PUT', body })
  },
  reset: () => request<AmazonTemplateInfo>('/amazon-template', { method: 'DELETE' }),
}

/**
 * Le catalogue SOM de référence, gardé sur le serveur. Il ne change que deux
 * fois par an ; chaque export VDB analysé ou converti se compare à lui.
 */
export const somReference = {
  get: () => request<SomSummary | null>('/som-reference'),
  upload: (file: File) => {
    const body = new FormData()
    body.append('file', file)
    return request<SomSummary>('/som-reference', { method: 'PUT', body })
  },
  update: (settings: SomReferenceSettings) =>
    request<SomSummary>('/som-reference', { method: 'PATCH', body: JSON.stringify(settings) }),
  remove: () => request<void>('/som-reference', { method: 'DELETE' }),
}

// --- Profils de règles -----------------------------------------------------

export const profiles = {
  list: () => request<ProfileSummary[]>('/profiles'),
  get: (id: string) => request<Profile>(`/profiles/${id}`),
  create: (name: string, rules?: RuleSet) =>
    request<Profile>('/profiles', { method: 'POST', body: JSON.stringify({ name, ...(rules && { rules }) }) }),
  update: (id: string, payload: { name?: string; rules?: RuleSet; note?: string }) =>
    request<Profile>(`/profiles/${id}`, { method: 'PATCH', body: JSON.stringify(payload) }),
  remove: (id: string) => request<void>(`/profiles/${id}`, { method: 'DELETE' }),
  setDefault: (id: string) => request<Profile>(`/profiles/${id}/default`, { method: 'POST' }),
  versions: (id: string) => request<ProfileVersionSummary[]>(`/profiles/${id}/versions`),
  restore: (id: string, version: number) =>
    request<Profile>(`/profiles/${id}/versions/${version}/restore`, { method: 'POST' }),
}

// --- Conversion ------------------------------------------------------------

export interface AnalyzeInput {
  file: File
  sheet?: string
  headerRow?: number
  /**
   * Le profil est transmis dès l'analyse : c'est lui qui porte les règles de
   * préparation du catalogue fournisseur. Sans lui, l'aperçu de l'étape 2
   * montrerait un tri que la conversion ne referait pas à l'identique.
   */
  profileId?: string
  /** Règles appliquées à cet appel seulement, sans rien enregistrer. */
  rules?: RuleSet
  /**
   * Comparer un export VDB au catalogue SOM gardé sur le serveur. Vrai par
   * défaut ; sans effet sur un fichier d'un autre fournisseur.
   */
  somCompare?: boolean
}

export interface ConvertInput extends AnalyzeInput {
  mapping: Record<string, number | null>
}

function catalogForm({ file, sheet = '', headerRow = 1, profileId, rules, somCompare = true }: AnalyzeInput): FormData {
  const body = new FormData()
  body.append('file', file)
  body.append('sheet', sheet)
  body.append('header_row', String(headerRow))
  if (profileId) body.append('profile_id', profileId)
  if (rules) body.append('rules', JSON.stringify(rules))
  body.append('som_compare', String(somCompare))
  return body
}

export const conversion = {
  analyze: (input: AnalyzeInput) =>
    request<CatalogAnalysis>('/catalog/analyze', { method: 'POST', body: catalogForm(input) }),
  run: ({ mapping, ...input }: ConvertInput) => {
    const body = catalogForm(input)
    body.append('mapping', JSON.stringify(mapping))
    return request<ConversionResponse>('/conversion', { method: 'POST', body })
  },
  /** Télécharge le catalogue chez le fournisseur, sans l'analyser. */
  fetchSupplier: () => request<SupplierDownload>('/catalog/fetch', { method: 'POST' }),
}
